import React, { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext(null);

const STORAGE_PREFIX = 'smartmenyu_cart_';

/**
 * Cart provider scoped to a restaurant and optionally a specific table.
 * When tableNumber is provided (URL pattern /qr-menu/:rid/t/:tableNumber),
 * the cart storage key is namespaced per-table so different tables don't share carts.
 */
export const CartProvider = ({ children, restaurantId, tableNumber: lockedTable = null }) => {
  const scopeKey = lockedTable ? `${restaurantId}_t${lockedTable}` : restaurantId;
  const storageKey = `${STORAGE_PREFIX}${scopeKey}`;

  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  const [tableNumber, setTableNumber] = useState(() => {
    if (lockedTable) return lockedTable;
    try {
      const t = localStorage.getItem(`${storageKey}_table`);
      return t ? parseInt(t, 10) : null;
    } catch {
      return null;
    }
  });

  const [isOpen, setIsOpen] = useState(false);

  // If table changes via URL, force-update state
  useEffect(() => {
    if (lockedTable && lockedTable !== tableNumber) {
      setTableNumber(lockedTable);
    }
  }, [lockedTable, tableNumber]);

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(items));
    } catch { /* ignore */ }
  }, [items, storageKey]);

  useEffect(() => {
    try {
      if (tableNumber) {
        localStorage.setItem(`${storageKey}_table`, String(tableNumber));
      }
    } catch { /* ignore */ }
  }, [tableNumber, storageKey]);

  const addItem = (menuItem, quantity = 1) => {
    setItems((prev) => {
      const existing = prev.find((i) => i.item_id === menuItem.id);
      if (existing) {
        return prev.map((i) =>
          i.item_id === menuItem.id ? { ...i, quantity: i.quantity + quantity } : i
        );
      }
      const price = parseFloat(menuItem.discount_price || menuItem.price || 0);
      return [
        ...prev,
        {
          item_id: menuItem.id,
          name_az: menuItem.name_az,
          name_en: menuItem.name_en,
          name_ru: menuItem.name_ru,
          image_url: menuItem.image_url,
          price,
          quantity,
          notes: '',
        },
      ];
    });
  };

  const updateQuantity = (itemId, delta) => {
    setItems((prev) =>
      prev
        .map((i) => (i.item_id === itemId ? { ...i, quantity: i.quantity + delta } : i))
        .filter((i) => i.quantity > 0)
    );
  };

  const removeItem = (itemId) => {
    setItems((prev) => prev.filter((i) => i.item_id !== itemId));
  };

  const updateNotes = (itemId, notes) => {
    setItems((prev) =>
      prev.map((i) => (i.item_id === itemId ? { ...i, notes } : i))
    );
  };

  const clear = () => {
    setItems([]);
    try {
      localStorage.removeItem(storageKey);
    } catch { /* ignore */ }
  };

  const subtotal = items.reduce((acc, it) => acc + it.price * it.quantity, 0);
  const totalQuantity = items.reduce((acc, it) => acc + it.quantity, 0);

  return (
    <CartContext.Provider
      value={{
        items,
        addItem,
        updateQuantity,
        removeItem,
        updateNotes,
        clear,
        subtotal,
        totalQuantity,
        tableNumber,
        setTableNumber,
        isTableLocked: !!lockedTable,
        isOpen,
        setIsOpen,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
};
